'use client';
import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, Check, MapPin, Clock, Star, Wallet,
  CreditCard, Shield, Plus, Minus, Car, UserCheck, Hotel, Info,
  CheckCircle2, Copy, AlertCircle, Lock, Calendar, Users
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const PACKAGE = {
  id: 'pkg-001',
  name: 'Maasai Mara Great Migration',
  operator: 'Savanna Adventures',
  region: 'Maasai Mara, Kenya',
  duration: 4,
  rating: 4.9,
  reviewCount: 312,
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16d806e5f-1772249664669.png',
  imageAlt: 'Wildebeest crossing the Mara River during Great Migration at golden hour',
  basePrice: 128000,
  depositRate: 0.30,
  includes: ['4 Game Drives', 'Full Board Accommodation', 'Park & Conservation Fees', 'Professional Guide', 'Drinking Water'],
  excludes: ['International Flights', 'Travel Insurance', 'Personal Items', 'Tips & Gratuities'],
};

const ADD_ONS = [
  {
    id: 'addon-transfer',
    icon: Car,
    name: 'Airport Transfer',
    description: 'Return pickup from JKIA / Wilson Airport in Nairobi',
    price: 8500,
    unit: 'per group',
    popular: true,
  },
  {
    id: 'addon-guide',
    icon: UserCheck,
    name: 'Private Guide',
    description: 'Dedicated senior naturalist guide — exclusive to your group',
    price: 15000,
    unit: 'per day',
    popular: false,
  },
  {
    id: 'addon-lodge',
    icon: Hotel,
    name: 'Lodge Upgrade',
    description: 'Upgrade to premium tented lodge from standard camp',
    price: 22000,
    unit: 'per person / night',
    popular: false,
  },
];

const AVAILABLE_DATES = [
  { id: 'date-apr15', start: 'Apr 15', end: 'Apr 18', year: '2026', spotsLeft: 3, popular: true },
  { id: 'date-apr22', start: 'Apr 22', end: 'Apr 25', year: '2026', spotsLeft: 7, popular: false },
  { id: 'date-may06', start: 'May 06', end: 'May 09', year: '2026', spotsLeft: 6, popular: false },
  { id: 'date-may20', start: 'May 20', end: 'May 23', year: '2026', spotsLeft: 8, popular: true },
  { id: 'date-jun03', start: 'Jun 03', end: 'Jun 06', year: '2026', spotsLeft: 4, popular: false },
  { id: 'date-jun17', start: 'Jun 17', end: 'Jun 20', year: '2026', spotsLeft: 9, popular: false },
];

type Step = 1 | 2 | 3;

interface BookingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  paymentMethod: 'wallet' | 'card';
  paymentType: 'deposit' | 'full';
  cardNumber: string;
  cardExpiry: string;
  cardCvv: string;
  cardName: string;
}

function calcAddOnLineTotal(addon: typeof ADD_ONS[0], groupSize: number, duration: number) {
  if (addon.unit === 'per group') return addon.price;
  if (addon.unit === 'per person / night') return addon.price * groupSize * duration;
  return addon.price;
}

export default function BookingFlow() {
  const [step, setStep] = useState<Step>(1);
  const [selectedDate, setSelectedDate] = useState<string>('date-apr15');
  const [groupSize, setGroupSize] = useState(2);
  const [selectedAddOns, setSelectedAddOns] = useState<Set<string>>(new Set(['addon-transfer']));
  const [paymentType, setPaymentType] = useState<'deposit' | 'full'>('deposit');
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'card'>('wallet');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [walletId, setWalletId] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const { user } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<BookingFormData>();

  useEffect(() => {
    if (!user) return;
    const fetchWallet = async () => {
      setWalletLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('wallets')
          .select('id, balance')
          .eq('user_id', user.id)
          .eq('wallet_role', 'customer')
          .eq('status', 'active')
          .single();
        if (!error && data) {
          setWalletBalance(Number(data.balance));
          setWalletId(data.id);
        }
      } catch {
        setWalletBalance(0);
      } finally {
        setWalletLoading(false);
      }
    };
    fetchWallet();
  }, [user]);

  const toggleAddOn = (id: string) => {
    setSelectedAddOns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const baseTotal = PACKAGE.basePrice * groupSize;
  const addOnTotal = ADD_ONS.filter((a) => selectedAddOns.has(a.id)).reduce(
    (sum, a) => sum + calcAddOnLineTotal(a, groupSize, PACKAGE.duration),
    0
  );
  const subtotal = baseTotal + addOnTotal;
  const depositAmount = Math.round(subtotal * PACKAGE.depositRate);
  const balanceDue = subtotal - depositAmount;
  const payNow = paymentType === 'deposit' ? depositAmount : subtotal;

  const onSubmit = async (formData: BookingFormData) => {
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      const ref = `OGS-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 89999)}`;
      const selectedDateObj = AVAILABLE_DATES.find((d) => d.id === selectedDate)!;

      // ── Wallet debit for wallet payments ──────────────────────────────────
      if (paymentMethod === 'wallet' && walletId) {
        if (walletBalance < payNow) {
          toast.error('Insufficient wallet balance. Please top up or use card payment.');
          setIsSubmitting(false);
          return;
        }

        // Record debit transaction
        const { error: txError } = await supabase.from('wallet_transactions').insert({
          wallet_id: walletId,
          ledger_type: 'debit',
          category: 'ride_payment',
          amount: payNow,
          description: `Safari booking deposit — ${PACKAGE.name}`,
          status: 'completed',
          metadata: {
            booking_reference: ref,
            package_id: PACKAGE.id,
            payment_type: paymentType,
          },
        });

        if (txError) {
          console.error('Wallet debit error:', txError.message);
          toast.error('Failed to process wallet payment. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // ── Insert booking ────────────────────────────────────────────────────
      const bookingPayload = {
        booking_reference: ref,
        user_id: user?.id ?? null,
        package_id: PACKAGE.id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        special_requests: formData.specialRequests || null,
        travel_date_start: selectedDateObj.start,
        travel_date_end: selectedDateObj.end,
        travel_year: selectedDateObj.year,
        group_size: groupSize,
        base_total: baseTotal,
        addon_total: addOnTotal,
        subtotal,
        deposit_amount: depositAmount,
        amount_paid: payNow,
        balance_due: paymentType === 'deposit' ? balanceDue : 0,
        payment_type: paymentType,
        payment_method: paymentMethod,
        booking_status: 'confirmed' as const,
      };

      const { data: booking, error: bookingError } = await supabase
        .from('safari_bookings')
        .insert(bookingPayload)
        .select('id, booking_reference')
        .single();

      if (bookingError) {
        console.error('Booking error:', bookingError.message);
        // Attempt to reverse wallet debit if booking failed
        if (paymentMethod === 'wallet' && walletId) {
          await supabase.from('wallet_transactions').insert({
            wallet_id: walletId,
            ledger_type: 'credit',
            category: 'refund',
            amount: payNow,
            description: `Refund — booking failed (${ref})`,
            status: 'completed',
            metadata: { reason: 'booking_insert_failed' },
          });
        }
        toast.error('Failed to save booking. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // ── Save add-ons ──────────────────────────────────────────────────────
      const selectedAddonsList = ADD_ONS.filter((a) => selectedAddOns.has(a.id));
      if (selectedAddonsList.length > 0) {
        const addonRows = selectedAddonsList.map((a) => ({
          booking_id: booking.id,
          addon_id: a.id,
          addon_name: a.name,
          addon_price: a.price,
          addon_unit: a.unit,
          line_total: calcAddOnLineTotal(a, groupSize, PACKAGE.duration),
        }));
        const { error: addonError } = await supabase.from('safari_booking_addons').insert(addonRows);
        if (addonError) console.error('Add-ons error:', addonError.message);
      }

      setBookingRef(booking.booking_reference);

      // ── Send confirmation email ───────────────────────────────────────────
      try {
        await fetch('/api/send-booking-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            bookingReference: booking.booking_reference,
            packageName: PACKAGE.name,
            operator: PACKAGE.operator,
            travelDateStart: selectedDateObj.start,
            travelDateEnd: selectedDateObj.end,
            travelYear: selectedDateObj.year,
            groupSize,
            paymentType,
            amountPaid: payNow,
            subtotal,
            depositAmount,
          }),
        });
      } catch (emailErr) {
        console.warn('[BookingFlow] Email send failed (non-blocking):', emailErr);
      }

      setIsSubmitting(false);
      setConfirmed(true);
      toast.success('Booking confirmed! Check your email for details.');
    } catch (err: any) {
      console.error('Unexpected booking error:', err.message);
      toast.error('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const steps = [
    { num: 1, label: 'Configure' },
    { num: 2, label: 'Add-ons' },
    { num: 3, label: 'Payment' },
  ];

  if (confirmed) {
    return (
      <BookingConfirmation
        bookingRef={bookingRef}
        pkg={PACKAGE}
        groupSize={groupSize}
        selectedDate={AVAILABLE_DATES.find((d) => d.id === selectedDate)!}
        payNow={payNow}
        paymentType={paymentType}
        balanceDue={paymentType === 'deposit' ? balanceDue : 0}
      />
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 sm:py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2 xl:col-span-3 order-2 lg:order-1">
          {/* Step Indicator */}
          <div className="flex items-center gap-2 sm:gap-4 mb-6 sm:mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={`step-${s.num}`}>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                      step > s.num
                        ? 'bg-safari-green text-white'
                        : step === s.num
                        ? 'bg-safari-yellow text-gray-900' :'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {step > s.num ? <Check size={13} /> : s.num}
                  </div>
                  <span
                    className={`text-xs sm:text-sm font-semibold ${
                      step === s.num ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 rounded-full ${
                      step > s.num ? 'bg-safari-green' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* ── Step 1: Configure ─────────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-5 sm:space-y-6 fade-in">
              {/* Travel Dates */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <div className="flex items-center gap-2 mb-4 sm:mb-5">
                  <Calendar size={16} className="text-safari-green" />
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Select Travel Dates</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {AVAILABLE_DATES.map((date) => (
                    <button
                      key={date.id}
                      onClick={() => setSelectedDate(date.id)}
                      className={`relative p-3 sm:p-4 rounded-xl border-2 text-left transition-all ${
                        selectedDate === date.id
                          ? 'border-safari-green bg-green-50' :'border-gray-200 hover:border-safari-yellow bg-white'
                      }`}
                    >
                      {date.popular && (
                        <span className="absolute -top-2 left-3 bg-safari-yellow text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                      <p className="text-xs sm:text-sm font-bold text-gray-900">
                        {date.start} — {date.end}
                      </p>
                      <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">{date.year}</p>
                      <p
                        className={`text-[11px] sm:text-xs font-semibold mt-1.5 ${
                          date.spotsLeft <= 3 ? 'text-red-600' : 'text-safari-green'
                        }`}
                      >
                        {date.spotsLeft} spots left
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Group Size */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <div className="flex items-center gap-2 mb-4 sm:mb-5">
                  <Users size={16} className="text-safari-green" />
                  <h2 className="text-base sm:text-lg font-bold text-gray-900">Group Size</h2>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                  <button
                    onClick={() => setGroupSize(Math.max(1, groupSize - 1))}
                    className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-safari-green hover:text-safari-green transition-all active:scale-90"
                  >
                    <Minus size={16} />
                  </button>
                  <div className="text-center">
                    <p className="text-3xl sm:text-4xl font-bold text-gray-900 tabular-nums">{groupSize}</p>
                    <p className="text-xs sm:text-sm text-gray-500">{groupSize === 1 ? 'person' : 'people'}</p>
                  </div>
                  <button
                    onClick={() => setGroupSize(Math.min(8, groupSize + 1))}
                    className="w-10 h-10 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-safari-green hover:text-safari-green transition-all active:scale-90"
                  >
                    <Plus size={16} />
                  </button>
                  <div className="p-3 bg-safari-sand rounded-xl">
                    <p className="text-xs text-gray-600">Per person</p>
                    <p className="text-base sm:text-lg font-bold text-gray-900 tabular-nums">
                      KES {PACKAGE.basePrice.toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                  <Info size={12} />
                  Group discounts apply for 5+ people.
                </p>
              </div>

              {/* Guest Details */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-5">Lead Guest Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                      First Name *
                    </label>
                    <input
                      {...register('firstName', { required: 'First name is required' })}
                      placeholder="Amara"
                      className={`w-full px-4 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                        errors.firstName
                          ? 'border-red-400 bg-red-50' :'border-gray-200 focus:border-safari-green'
                      }`}
                    />
                    {errors.firstName && (
                      <p className="text-xs text-red-500 mt-1">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                      Last Name *
                    </label>
                    <input
                      {...register('lastName', { required: 'Last name is required' })}
                      placeholder="Osei"
                      className={`w-full px-4 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                        errors.lastName
                          ? 'border-red-400 bg-red-50' :'border-gray-200 focus:border-safari-green'
                      }`}
                    />
                    {errors.lastName && (
                      <p className="text-xs text-red-500 mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                      Email Address *
                    </label>
                    <input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' },
                      })}
                      type="email"
                      placeholder="amara@example.com"
                      className={`w-full px-4 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                        errors.email
                          ? 'border-red-400 bg-red-50' :'border-gray-200 focus:border-safari-green'
                      }`}
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                      Phone Number *
                    </label>
                    <input
                      {...register('phone', { required: 'Phone number is required' })}
                      type="tel"
                      placeholder="+254 7XX XXX XXX"
                      className={`w-full px-4 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                        errors.phone
                          ? 'border-red-400 bg-red-50' :'border-gray-200 focus:border-safari-green'
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                      Special Requests
                    </label>
                    <p className="text-xs text-gray-400 mb-1.5">
                      Dietary requirements, accessibility needs, celebration arrangements, etc.
                    </p>
                    <textarea
                      {...register('specialRequests')}
                      rows={3}
                      placeholder="e.g. Vegetarian meals, wheelchair accessible vehicle, anniversary celebration..."
                      className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm text-gray-800 outline-none transition-all resize-none"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 px-5 sm:px-6 py-3 bg-safari-green text-white font-semibold rounded-xl hover:bg-green-800 transition-all active:scale-95 text-sm sm:text-base"
                >
                  Continue to Add-ons
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Add-ons ───────────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-5 sm:space-y-6 fade-in">
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Enhance Your Safari</h2>
                <p className="text-xs sm:text-sm text-gray-500 mb-5">
                  Optional add-ons to make your experience unforgettable.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  {ADD_ONS.map((addon) => {
                    const isSelected = selectedAddOns.has(addon.id);
                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddOn(addon.id)}
                        className={`relative flex items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-safari-green bg-green-50' :'border-gray-200 hover:border-safari-yellow bg-white'
                        }`}
                      >
                        {addon.popular && (
                          <span className="absolute -top-2 left-4 bg-safari-yellow text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                            Most Added
                          </span>
                        )}
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-safari-green text-white' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          <addon.icon size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-bold text-gray-900">{addon.name}</p>
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                isSelected ? 'border-safari-green bg-safari-green' : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <Check size={11} className="text-white" />}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 mb-2">{addon.description}</p>
                          <p className="text-sm font-bold text-safari-green tabular-nums">
                            + KES {addon.price.toLocaleString()}{' '}
                            <span className="text-xs font-normal text-gray-400">{addon.unit}</span>
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-4 sm:px-5 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:border-gray-400 transition-all text-sm"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-2 px-5 sm:px-6 py-3 bg-safari-green text-white font-semibold rounded-xl hover:bg-green-800 transition-all active:scale-95 text-sm"
                >
                  Continue to Payment
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Payment ───────────────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-5 sm:space-y-6 fade-in">
              {/* Payment Type Toggle */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Payment Option</h2>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-5">
                  Pay a 30% deposit now and settle the balance before your departure.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Deposit Option */}
                  <button
                    onClick={() => setPaymentType('deposit')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      paymentType === 'deposit' ?'border-safari-green bg-green-50' :'border-gray-200 hover:border-safari-yellow'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-gray-900">Pay Deposit</p>
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          paymentType === 'deposit' ?'border-safari-green bg-safari-green' :'border-gray-300'
                        }`}
                      >
                        {paymentType === 'deposit' && (
                          <div className="w-full h-full rounded-full bg-white scale-50" />
                        )}
                      </div>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-safari-green tabular-nums">
                      KES {depositAmount.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">30% now</p>
                    {/* Balance due pill */}
                    <div className="mt-3 flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                      <Lock size={11} className="text-amber-600 flex-shrink-0" />
                      <span className="text-[11px] font-semibold text-amber-700">
                        Balance due: KES {balanceDue.toLocaleString()}
                      </span>
                    </div>
                  </button>

                  {/* Full Payment Option */}
                  <button
                    onClick={() => setPaymentType('full')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      paymentType === 'full' ?'border-safari-green bg-green-50' :'border-gray-200 hover:border-safari-yellow'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-gray-900">Pay in Full</p>
                      <div
                        className={`w-4 h-4 rounded-full border-2 ${
                          paymentType === 'full' ?'border-safari-green bg-safari-green' :'border-gray-300'
                        }`}
                      >
                        {paymentType === 'full' && (
                          <div className="w-full h-full rounded-full bg-white scale-50" />
                        )}
                      </div>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-safari-green tabular-nums">
                      KES {subtotal.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">No balance due</p>
                    <div className="mt-3 flex items-center gap-1.5 bg-green-50 border border-green-100 rounded-lg px-2.5 py-1.5">
                      <CheckCircle2 size={11} className="text-safari-green flex-shrink-0" />
                      <span className="text-[11px] font-semibold text-safari-green">
                        Fully paid · Instant confirmation
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-card">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-5">Payment Method</h2>
                <div className="flex gap-2 sm:gap-3 mb-4 sm:mb-5">
                  <button
                    onClick={() => setPaymentMethod('wallet')}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 text-xs sm:text-sm font-semibold transition-all ${
                      paymentMethod === 'wallet'
                        ? 'border-safari-green bg-green-50 text-safari-green' :'border-gray-200 text-gray-600 hover:border-safari-yellow'
                    }`}
                  >
                    <Wallet size={15} />
                    Onthego Wallet
                  </button>
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 text-xs sm:text-sm font-semibold transition-all ${
                      paymentMethod === 'card' ?'border-safari-green bg-green-50 text-safari-green' :'border-gray-200 text-gray-600 hover:border-safari-yellow'
                    }`}
                  >
                    <CreditCard size={15} />
                    Card Payment
                  </button>
                </div>

                {/* Wallet Panel */}
                {paymentMethod === 'wallet' && (
                  <div className="p-4 bg-safari-sand rounded-xl fade-in space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Wallet size={16} className="text-safari-green" />
                        <span className="text-sm font-semibold text-gray-800">Wallet Balance</span>
                      </div>
                      {walletLoading ? (
                        <div className="w-4 h-4 border-2 border-safari-green/30 border-t-safari-green rounded-full animate-spin" />
                      ) : (
                        <span className="text-base sm:text-lg font-bold text-safari-green tabular-nums">
                          KES {walletBalance.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Deposit split breakdown */}
                    {paymentType === 'deposit' && !walletLoading && (
                      <div className="bg-white rounded-lg p-3 space-y-1.5 border border-gray-100">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Pay now (30% deposit)</span>
                          <span className="font-bold text-safari-green tabular-nums">
                            KES {depositAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Balance due before trip</span>
                          <span className="font-semibold text-amber-600 tabular-nums">
                            KES {balanceDue.toLocaleString()}
                          </span>
                        </div>
                        <div className="border-t border-gray-100 pt-1.5 flex justify-between text-xs">
                          <span className="text-gray-500">Remaining wallet balance</span>
                          <span
                            className={`font-bold tabular-nums ${
                              walletBalance - depositAmount >= 0 ? 'text-gray-800' : 'text-red-600'
                            }`}
                          >
                            KES {(walletBalance - depositAmount).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {!walletLoading && walletBalance < payNow ? (
                      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-600 font-medium">
                          Insufficient balance. You need KES{' '}
                          {(payNow - walletBalance).toLocaleString()} more. Please top up your
                          wallet or use card payment.
                        </p>
                      </div>
                    ) : (
                      !walletLoading && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle2 size={14} className="text-safari-green flex-shrink-0" />
                          <p className="text-xs text-safari-green font-medium">
                            Sufficient balance to cover this payment.
                          </p>
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Card Panel */}
                {paymentMethod === 'card' && (
                  <div className="space-y-4 fade-in">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                        Card Number *
                      </label>
                      <input
                        {...register('cardNumber')}
                        placeholder="4111 1111 1111 1111"
                        className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all font-mono"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                          Expiry *
                        </label>
                        <input
                          {...register('cardExpiry')}
                          placeholder="MM / YY"
                          className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                          CVV *
                        </label>
                        <input
                          {...register('cardCvv')}
                          placeholder="···"
                          type="password"
                          maxLength={4}
                          className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">
                        Cardholder Name *
                      </label>
                      <input
                        {...register('cardName')}
                        placeholder="AMARA OSEI"
                        className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-4 p-3 bg-gray-50 rounded-lg">
                  <Shield size={13} className="text-safari-green flex-shrink-0" />
                  <p className="text-xs text-gray-500">
                    Your payment is secured with 256-bit SSL encryption via Onthego Pay.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-2 px-4 sm:px-5 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:border-gray-400 transition-all text-sm"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    isSubmitting ||
                    walletLoading ||
                    (paymentMethod === 'wallet' && walletBalance < payNow)
                  }
                  className="flex items-center gap-2 px-5 sm:px-8 py-3 bg-safari-yellow text-gray-900 font-bold rounded-xl hover:bg-yellow-400 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed min-w-[160px] sm:min-w-[180px] justify-center text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Shield size={15} />
                      Pay KES {payNow.toLocaleString()}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Booking Summary Sidebar ───────────────────────────────────── */}
        <div className="lg:col-span-1 xl:col-span-1 order-1 lg:order-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            {/* Package Card */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-card">
              <div className="relative h-32 sm:h-36">
                <AppImage
                  src={PACKAGE.image}
                  alt={PACKAGE.imageAlt}
                  fill
                  className="object-cover"
                  sizes="400px"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <p className="text-white text-sm font-bold leading-tight">{PACKAGE.name}</p>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                  <MapPin size={11} />
                  {PACKAGE.region}
                  <span className="text-gray-300">·</span>
                  <Clock size={11} />
                  {PACKAGE.duration} days
                </div>
                <div className="flex items-center gap-1">
                  <Star size={12} className="fill-safari-yellow text-safari-yellow" />
                  <span className="text-xs font-bold text-gray-800">{PACKAGE.rating}</span>
                  <span className="text-xs text-gray-400">({PACKAGE.reviewCount} reviews)</span>
                </div>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 shadow-card">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Price Breakdown</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base × {groupSize}</span>
                  <span className="font-semibold tabular-nums">KES {baseTotal.toLocaleString()}</span>
                </div>
                {ADD_ONS.filter((a) => selectedAddOns.has(a.id)).map((a) => (
                  <div key={`price-${a.id}`} className="flex justify-between">
                    <span className="text-gray-600 truncate mr-2">{a.name}</span>
                    <span className="font-semibold tabular-nums text-safari-green whitespace-nowrap">
                      + KES {calcAddOnLineTotal(a, groupSize, PACKAGE.duration).toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t border-gray-100 pt-2.5">
                  <div className="flex justify-between font-bold text-gray-900">
                    <span>Total</span>
                    <span className="tabular-nums">KES {subtotal.toLocaleString()}</span>
                  </div>
                </div>

                {/* Deposit split */}
                {paymentType === 'deposit' && (
                  <div className="bg-safari-sand rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between text-xs font-semibold text-safari-green">
                      <span>Pay now (30%)</span>
                      <span className="tabular-nums">KES {depositAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs text-amber-600 font-semibold">
                      <span className="flex items-center gap-1">
                        <Lock size={10} />
                        Balance due
                      </span>
                      <span className="tabular-nums">KES {balanceDue.toLocaleString()}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">Due 7 days before departure</p>
                  </div>
                )}
              </div>
            </div>

            {/* Includes */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 shadow-card">
              <h3 className="text-sm font-bold text-gray-900 mb-3">What&apos;s Included</h3>
              <ul className="space-y-1.5">
                {PACKAGE.includes.map((inc) => (
                  <li key={`inc-${inc}`} className="flex items-center gap-2 text-xs text-gray-600">
                    <Check size={12} className="text-safari-green flex-shrink-0" />
                    {inc}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 font-semibold mb-1.5">Not included:</p>
                {PACKAGE.excludes.map((exc) => (
                  <p key={`exc-${exc}`} className="text-xs text-gray-400 flex items-center gap-1.5">
                    <span className="w-1 h-1 bg-gray-300 rounded-full" />
                    {exc}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Confirmation Screen ────────────────────────────────────────────────────────

interface ConfirmationProps {
  bookingRef: string;
  pkg: typeof PACKAGE;
  groupSize: number;
  selectedDate: (typeof AVAILABLE_DATES)[0];
  payNow: number;
  paymentType: 'deposit' | 'full';
  balanceDue: number;
}

function BookingConfirmation({
  bookingRef,
  pkg,
  groupSize,
  selectedDate,
  payNow,
  paymentType,
  balanceDue,
}: ConfirmationProps) {
  const [copied, setCopied] = useState(false);

  const copyRef = () => {
    navigator.clipboard.writeText(bookingRef);
    setCopied(true);
    toast.success('Booking reference copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5 sm:mb-6">
        <CheckCircle2 size={36} className="text-safari-green" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
      <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8">
        Your safari adventure is booked. A confirmation email has been sent to your registered address.
      </p>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-card mb-6 text-left">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Booking Reference
          </p>
          <button
            onClick={copyRef}
            className="flex items-center gap-1.5 text-xs font-semibold text-safari-green hover:text-green-700 transition-colors"
          >
            <Copy size={13} />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="text-xl sm:text-2xl font-bold text-gray-900 font-mono mb-4">{bookingRef}</p>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Package</p>
            <p className="font-semibold text-gray-800 text-xs sm:text-sm">{pkg.name}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Travel Dates</p>
            <p className="font-semibold text-gray-800 text-xs sm:text-sm">
              {selectedDate.start} — {selectedDate.end}, {selectedDate.year}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Group Size</p>
            <p className="font-semibold text-gray-800 text-xs sm:text-sm">
              {groupSize} {groupSize === 1 ? 'person' : 'people'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Amount Paid</p>
            <p className="font-bold text-safari-green tabular-nums text-xs sm:text-sm">
              KES {payNow.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Deposit balance due notice */}
        {paymentType === 'deposit' && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lock size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-700 mb-0.5">
                  Balance Due: KES {balanceDue.toLocaleString()}
                </p>
                <p className="text-xs text-amber-600">
                  You paid a 30% deposit. The remaining balance is due 7 days before your trip.
                  You&apos;ll receive a reminder email.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href="/savings"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-safari-green text-safari-green font-semibold rounded-xl hover:bg-green-50 transition-all text-sm"
        >
          <Wallet size={15} />
          Save for Balance Due
        </a>
        <a
          href="/safari-packages-listing"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-safari-green text-white font-semibold rounded-xl hover:bg-green-800 transition-all text-sm"
        >
          Explore More Safaris
        </a>
      </div>
    </div>
  );
}